import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { ProjectPhaseStatus } from "../../enum/common";
import { ICreateProjectPhase } from "../../models/Research";
import { useCreateProjectPhases } from "../../query/ReseachQuery";
import { notify } from "../../utils/toast";

interface IModalAddPhaseProps {
  open: boolean;
  onCancel: () => void;
  projectId: number;
}

const ModalAddProjectPhase = ({
  open,
  onCancel,
  projectId,
}: IModalAddPhaseProps) => {
  const [form] = Form.useForm();
  const createPhaseMutation = useCreateProjectPhases();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data: ICreateProjectPhase = {
        projectId,
        phaseOrder: values.phaseOrder,
        phaseName: values.phaseName,
        startDate: values.startDate.format("YYYY-MM-DD"),
        endDate: values.endDate.format("YYYY-MM-DD"),
        status: values.status,
        progress: values.progress || 0,
        projectPhaseMilestones: (values.projectPhaseMilestones || []).map(
          (m: any) => ({
            milestoneName: m.milestoneName,
            targetDate: dayjs(m.targetDate).format("YYYY-MM-DD"),
            isCompleted: m.isCompleted || false,
            completedDate: m.completedDate
              ? dayjs(m.completedDate).format("YYYY-MM-DD")
              : null,
          }),
        ),
      };

      await createPhaseMutation.mutateAsync(data);
      notify.success("Thêm giai đoạn thành công");
      onCancel();
      form.resetFields();
    } catch (error) {
      notify.error("Thêm giai đoạn thất bại");
    }
  };

  const getColumns = (remove: (index: number) => void) => [
    {
      title: "Tên cột mốc",
      dataIndex: "milestoneName",
      key: "milestoneName",
      render: (_: any, record: any) => (
        <Form.Item
          name={[record.name, "milestoneName"]}
          rules={[{ required: true, message: "Vui lòng nhập tên cột mốc" }]}
          style={{ marginBottom: 0 }}
        >
          <Input placeholder="Nhập tên cột mốc" />
        </Form.Item>
      ),
    },
    {
      title: "Ngày mục tiêu",
      dataIndex: "targetDate",
      key: "targetDate",
      width: 200,
      render: (_: any, record: any) => (
        <Form.Item
          name={[record.name, "targetDate"]}
          rules={[{ required: true, message: "Vui lòng chọn ngày" }]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
        </Form.Item>
      ),
    },
    {
      title: "Hoàn thành",
      dataIndex: "isCompleted",
      key: "isCompleted",
      width: 120,
      render: (_: any, record: any) => (
        <Form.Item
          name={[record.name, "isCompleted"]}
          initialValue={false}
          style={{ marginBottom: 0 }}
        >
          <Select style={{ width: "100%" }}>
            <Select.Option value={true}>Có</Select.Option>
            <Select.Option value={false}>Không</Select.Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: "Ngày hoàn thành",
      dataIndex: "completedDate",
      key: "completedDate",
      width: 200,
      render: (_: any, record: any) => (
        <Form.Item
          name={[record.name, "completedDate"]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
        </Form.Item>
      ),
    },
    {
      title: "",
      key: "action",
      width: 60,
      render: (_: any, record: any) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => remove(record.name)}
        />
      ),
    },
  ];

  return (
    <Modal
      title="Thêm giai đoạn mới"
      open={open}
      onCancel={() => {
        onCancel();
        form.resetFields();
      }}
      width={1000}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onCancel();
            form.resetFields();
          }}
        >
          Hủy
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={createPhaseMutation.isPending}
        >
          Thêm
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <Form.Item
              label="Thứ tự giai đoạn"
              name="phaseOrder"
              rules={[{ required: true, message: "Vui lòng nhập thứ tự" }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>

            <Form.Item
              label="Tên giai đoạn"
              name="phaseName"
              rules={[
                { required: true, message: "Vui lòng nhập tên giai đoạn" },
              ]}
            >
              <Input placeholder="Nhập tên giai đoạn" />
            </Form.Item>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <Form.Item
              label="Ngày bắt đầu"
              name="startDate"
              rules={[
                { required: true, message: "Vui lòng chọn ngày bắt đầu" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
            </Form.Item>

            <Form.Item
              label="Ngày kết thúc"
              name="endDate"
              rules={[
                { required: true, message: "Vui lòng chọn ngày kết thúc" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
            </Form.Item>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <Form.Item
              label="Trạng thái"
              name="status"
              initialValue={ProjectPhaseStatus.PENDING}
              rules={[{ required: true, message: "Vui lòng chọn trạng thái" }]}
            >
              <Select>
                <Select.Option value={ProjectPhaseStatus.PENDING}>
                  Chờ thực hiện
                </Select.Option>
                <Select.Option value={ProjectPhaseStatus.IN_PROGRESS}>
                  Đang thực hiện
                </Select.Option>
                <Select.Option value={ProjectPhaseStatus.COMPLETED}>
                  Hoàn thành
                </Select.Option>
                <Select.Option value={ProjectPhaseStatus.DELAYED}>
                  Trễ hạn
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Tiến độ (%)"
              name="progress"
              initialValue={0}
              rules={[{ required: true, message: "Vui lòng nhập tiến độ" }]}
            >
              <InputNumber style={{ width: "100%" }} min={0} max={100} />
            </Form.Item>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <Typography.Text strong>Cột mốc quan trọng</Typography.Text>
            </div>

            <Form.List name="projectPhaseMilestones">
              {(fields, { add, remove }) => (
                <>
                  {fields.length > 0 ? (
                    <Table
                      dataSource={fields}
                      columns={getColumns(remove)}
                      pagination={false}
                      rowKey="key"
                      size="small"
                    />
                  ) : (
                    <Empty
                      description="Chưa có cột mốc nào"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 12 }}
                  >
                    Thêm cột mốc
                  </Button>
                </>
              )}
            </Form.List>
          </div>
        </Space>
      </Form>
    </Modal>
  );
};

export default ModalAddProjectPhase;
